import { useState, useEffect } from 'react';
import { api } from '../api';
import { formatBytes, timeAgo, formatDateTime, DEFAULT_TZ, useSortable, SortTh, useIsMobile } from '../utils';
import { RunProgressLine, RunStatusBadge } from '../components/RunProgress';
import JobHoneypotProtection from '../components/JobHoneypotProtection';

const statusBadge = {
  ok:      { bg: 'rgba(0,229,160,0.12)',   color: 'var(--green)',  label: 'OK' },
  warning: { bg: 'rgba(255,209,102,0.12)', color: 'var(--yellow)', label: 'WARN' },
  error:   { bg: 'rgba(255,77,109,0.12)',  color: 'var(--red)',    label: 'ERROR' },
  running: { bg: 'rgba(0,212,255,0.12)',   color: 'var(--accent)', label: 'EN CURSO' },
};

export function Badge({ s }) {
  const b = statusBadge[s] || statusBadge.ok;
  return <span style={{ display: 'inline-block', padding: '3px 8px', fontSize: 10, fontFamily: 'var(--mono)', letterSpacing: 1, textTransform: 'uppercase', borderRadius: 2, background: b.bg, color: b.color }}>{b.label}</span>;
}

function DefaultBadge({ r }) { return <Badge s={r.status} />; }

export default function Jobs() {
  const isMobile              = useIsMobile();
  const [runs, setRuns]       = useState([]);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await api.getRecentRuns(50);
      setRuns(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Auto-refresh cada 5s mientras haya runs en curso; se detiene cuando todos terminan
  useEffect(() => {
    load();
    const t = setInterval(() => {
      setRuns(prev => {
        const hasRunning = prev.some(r => r.status === 'running');
        if (hasRunning) load();
        return prev;
      });
    }, 5000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  const filtered = runs.filter(r =>
    r.job_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.client?.toLowerCase().includes(search.toLowerCase())
  );
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'started_at', 'desc');

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>CARGANDO...</div>;

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'flex-end', justifyContent: 'space-between', marginBottom: 24, gap: isMobile ? 12 : 0 }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 20 }}>Jobs</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{runs.length} runs registrados</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="buscar job o cliente..."
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 14px', fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', flex: 1, minWidth: 0 }} />
          <button onClick={load} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', whiteSpace: 'nowrap' }}>⟳ ACT.</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 40, textAlign: 'center', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
          Sin runs registrados todavía.
        </div>
      ) : isMobile ? (
        <div>
          {sorted.map(r => (
            <div key={r.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>JOB</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{r.job_name}</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>CLIENTE</div>
                  <div style={{ fontSize: 13 }}>{r.client}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>SERVIDOR</div>
                  <div style={{ fontSize: 13 }}>{r.server}</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 2 }}>INICIO</div>
              <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--muted)', marginBottom: 10 }}>{formatDateTime(r.started_at, r.server_timezone || DEFAULT_TZ)}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{formatBytes(r.size_bytes)}</div>
                <RunStatusBadge r={r} isMobile={isMobile} DefaultBadge={DefaultBadge} />
              </div>
              <RunProgressLine r={r} tz={r.server_timezone || DEFAULT_TZ} />
              <JobHoneypotProtection jobType={r.job_type} status={r.job_honeypot_status} jobId={r.job_id} agentVersion={r.server_agent_version} running={r.job_running} onChanged={load} />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg)' }}>
                {[
                  ['Job',        'job_name'],
                  ['Cliente',    'client'],
                  ['Servidor',   'server'],
                  ['Inicio',     'started_at'],
                  ['Duración',   'duration_seconds'],
                  ['Procesados', 'size_bytes'],
                  ['Añadidos',   'data_added_bytes'],
                  ['Estado',     'status'],
                ].map(([label, col]) => (
                  <SortTh key={col} label={label} colKey={col} sortKey={sortKey} sortDir={sortDir} onToggle={toggle}
                    style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={r.id} style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border)' : 'none' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '14px 16px', fontWeight: 600 }}>{r.job_name}</td>
                  <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 12 }}>{r.client}</td>
                  <td style={{ padding: '14px 16px', color: 'var(--muted)', fontSize: 12 }}>{r.server}</td>
                  <td style={{ padding: '14px 16px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}
                      title={timeAgo(r.started_at)}>
                    {formatDateTime(r.started_at, r.server_timezone || DEFAULT_TZ)}
                  </td>
                  <td style={{ padding: '14px 16px', fontFamily: 'var(--mono)', fontSize: 12 }}>{r.duration_seconds ? r.duration_seconds + 's' : '—'}</td>
                  <td style={{ padding: '14px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)' }}>{formatBytes(r.size_bytes)}</td>
                  <td style={{ padding: '14px 16px', fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--green)' }}>{formatBytes(r.data_added_bytes)}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <RunStatusBadge r={r} isMobile={isMobile} DefaultBadge={DefaultBadge} />
                    <RunProgressLine r={r} tz={r.server_timezone || DEFAULT_TZ} />
                    <JobHoneypotProtection jobType={r.job_type} status={r.job_honeypot_status} jobId={r.job_id} agentVersion={r.server_agent_version} running={r.job_running} onChanged={load} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
